import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import _map from 'lodash/map';
import _includes from 'lodash/includes';
import _keys from 'lodash/keys';
import _isEmpty from 'lodash/isEmpty';
import Question from 'styles/blocks/question';
import { Answer } from 'components/consultation/answers';
import { STRINGS } from 'config';
import { VALIDATION_MESSAGES } from 'common/validationMessages';
import { NextButton } from 'components/consultation/answers/nextButton';
import { AnimationControl } from './animationControl';

const { SHOW_PATIENT_SEARCH } = STRINGS.TAGS.QUESTION;

function checkShouldShowElitePatientSearch(isPatientSearchQuestion) {
  window?.workflowFrontendIntegration?.shouldShowPatientSearchResults(isPatientSearchQuestion);
}

const QuestionContainer = animated(Question);
const Error = animated(Question.Error);

const getGenericErrorMessage = (errorCode, maxLength, minLength) => {
  if (!errorCode || !_includes(_keys(VALIDATION_MESSAGES.GENERIC), errorCode)) {
    return null;
  }
  return VALIDATION_MESSAGES.getTemplatedValidation(VALIDATION_MESSAGES.GENERIC[errorCode], { maxLength, minLength, fieldName: 'This answer' });
};

export function AnimatedError({ errorMessage, index, errorRef }) {
  const [validationHeight] = useSpring(() => ({ from: { opacity: 0 }, to: { opacity: 1 } }));
  return <Error style={validationHeight} ref={index === 0 ? errorRef : null}>{errorMessage}</Error>;
}

export const renderQuestionComponent = question => ({
  style,
  isMounted,
  setParentHeight,
  validationErrors,
  isClinicalReviewMode,
  onClickNextQuestion,
  isCompoundQuestion,
  containerWidth,
  isDisclaimerQuestion,
  publicHealthAlertHtml,
  containerRef,
  isAnimationDisabled,
  onToggleAnimation,
  prefersReducedMotion,
  federatedLoginProvider,
  isLoggedIn,
  product,
  previousAnswers,
}) => {
  const ref = useRef();
  const errorRef = useRef();
  // todo: This doesn't seem like a good thing
  function createMarkup(text) {
    return { __html: text };
  }

  if (!question.questionId) {
    return null;
  }

  useLayoutEffect(() => {
    setParentHeight(ref.current.clientHeight);
  }, [setParentHeight]);

  const handleSubmit = event => {
    event.preventDefault();
  };
  const firstValidationError = validationErrors[0];
  useEffect(() => {
    // Scroll question container div so validation errors are visible
    if (!isCompoundQuestion && firstValidationError && errorRef && errorRef.current) {
      const paddingTop = 100;
      const validationPosition = errorRef.current.offsetTop;
      const isValidationAtPageTop = validationPosition <= paddingTop;
      // eslint-disable-next-line no-param-reassign
      containerRef.current.scrollTop = isValidationAtPageTop ? 0 : validationPosition - paddingTop;
    }
  }, [firstValidationError, errorRef, isCompoundQuestion, containerRef]);

  const isPatientSearchQuestion = question?.tags?.includes(SHOW_PATIENT_SEARCH);

  useEffect(() => {
    checkShouldShowElitePatientSearch(isPatientSearchQuestion);
  }, [isPatientSearchQuestion]);

  const FEDERATED_LOGIN_PROVIDER = STRINGS.FEDERATED_LOGIN_PROVIDER[federatedLoginProvider] || {};

  const [primaryCareOrESpecialist] = useState(() => product === STRINGS.PRODUCT.PRIMARY_CARE || product === STRINGS.PRODUCT.E_SPECIALIST);
  const [isGuidanceTextShownAboveQuestion] = useState(() => primaryCareOrESpecialist || !(_includes(STRINGS.E_TRIAGE_QUESTION_GUIDANCE_IN_ANSWER, question.type)));
  const [isPreviouslyAnswered] = useState(() => previousAnswers && !_isEmpty(previousAnswers) && !_isEmpty(previousAnswers[0]));
  const [isETriageSingleSelect] = useState(() => !primaryCareOrESpecialist && question.type === STRINGS.QUESTION_TYPE.SINGLE);

  return (
    <QuestionContainer style={style} containerWidth={containerWidth} onSubmit={handleSubmit} noValidate>
      <Question.InnerContainer ref={ref} shouldRepaint={isMounted}>
        {isLoggedIn && question.tags && _includes(question.tags, STRINGS.TAGS.QUESTION.LOGIN_PROVIDED_DATA) && (
          FEDERATED_LOGIN_PROVIDER.DISPLAY_NAME.endsWith('NHS login') ? (
            <Question.AlertLabel>
              Your details have been pre-filled from your NHS login. <a href="https://settings.login.nhs.uk/">Manage your {FEDERATED_LOGIN_PROVIDER.DISPLAY_NAME} account</a>
            </Question.AlertLabel>
          ) : (
            <Question.AlertLabel>Your details have been pre-filled from your {FEDERATED_LOGIN_PROVIDER.DISPLAY_NAME}</Question.AlertLabel>
          ))}
        {isDisclaimerQuestion && <div className="react-public-health-alert" dangerouslySetInnerHTML={createMarkup(publicHealthAlertHtml)} />}
        {isClinicalReviewMode && primaryCareOrESpecialist && <Question.Text>{question.questionId}</Question.Text>}
        {!isCompoundQuestion && (question.type !== STRINGS.QUESTION_TYPE.LOGIN) && (
          <Question.Text>
            <Question.ReceivedHTML dangerouslySetInnerHTML={createMarkup(question.text)} />
          </Question.Text>
        )}
        {question.guidanceScript && isGuidanceTextShownAboveQuestion && <Question.HelpLabel dangerouslySetInnerHTML={createMarkup(question.guidanceScript)} />}
        {!isCompoundQuestion && _map(validationErrors, (error, i) => {
          const genericErrorMessage = getGenericErrorMessage(error.code, question.maxLength, question.minLength);
          return (
            <AnimatedError
              key={`animated-question-error-${i}`}
              errorMessage={error.localError || genericErrorMessage || error.message}
              index={i}
              errorRef={errorRef}
            />
          );
        })}
        <Answer
          question={question}
          isMounted={isMounted}
          onClickNextQuestion={onClickNextQuestion}
          questionContainerRef={containerRef}
          isGuidanceTextShownAboveQuestion={isGuidanceTextShownAboveQuestion}
        />
        {isETriageSingleSelect && isPreviouslyAnswered && <NextButton dataAutomation={`nextButton-${question.questionId}`} onClick={onClickNextQuestion} />}
        {isDisclaimerQuestion && !prefersReducedMotion && primaryCareOrESpecialist && (
          <AnimationControl isAnimationDisabled={isAnimationDisabled} onToggleAnimation={onToggleAnimation}>This form uses screen animation. Turn off animation</AnimationControl>
        )}
      </Question.InnerContainer>
    </QuestionContainer>
  );
};
